import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuPortal } from '@/components/ui/dropdown-menu';
import { MoreVertical, Edit, Copy, Trash2, FileDown, FileText } from 'lucide-react';
import { documentAPI } from '@/services/api';
import { toast } from 'sonner';

const LessonPlanCard = ({ plan, onDelete, onDuplicate }) => {
  const navigate = useNavigate();

  const handleExport = async (format, orientation = 'landscape') => {
    try {
      const { data } = format === 'docx' 
        ? await documentAPI.generateDOCX(plan.lesson_plan_id, orientation)
        : await documentAPI.generatePDF(plan.lesson_plan_id, orientation);
      
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lesson_plan_${plan.subject}_${plan.class_name}_${orientation}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${format.toUpperCase()} (${orientation}) downloaded`);
    } catch (error) {
      toast.error(`Failed to generate ${format.toUpperCase()}`);
    }
  };

  return (
    <Card
      data-testid={`lesson-plan-card-${plan.lesson_plan_id}`}
      className="p-6 border border-stone-200 hover:-translate-y-1 transition-transform duration-200 ease-out cursor-pointer group"
      onClick={() => navigate(`/builder/${plan.lesson_plan_id}`)}
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-[#1C1917] mb-1">{plan.subject}</h3>
          <p className="text-sm text-stone-600">{plan.class_name} • {plan.chapter}</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button data-testid={`lesson-plan-menu-${plan.lesson_plan_id}`} variant="ghost" size="sm">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/builder/${plan.lesson_plan_id}`); }}>
              <Edit className="w-4 h-4 mr-2" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDuplicate(plan.lesson_plan_id); }}>
              <Copy className="w-4 h-4 mr-2" /> Duplicate
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <FileDown className="w-4 h-4 mr-2" /> Export DOCX
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <DropdownMenuItem data-testid="export-docx-landscape" onClick={(e) => { e.stopPropagation(); handleExport('docx', 'landscape'); }}>
                    Landscape
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="export-docx-portrait" onClick={(e) => { e.stopPropagation(); handleExport('docx', 'portrait'); }}>
                    Portrait
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <FileText className="w-4 h-4 mr-2" /> Export PDF
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <DropdownMenuItem data-testid="export-pdf-landscape" onClick={(e) => { e.stopPropagation(); handleExport('pdf', 'landscape'); }}>
                    Landscape
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="export-pdf-portrait" onClick={(e) => { e.stopPropagation(); handleExport('pdf', 'portrait'); }}>
                    Portrait
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(plan.lesson_plan_id); }} className="text-red-600">
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="space-y-2 text-sm text-stone-600">
        <p><span className="font-medium">Teacher:</span> {plan.teacher_name}</p>
        <p><span className="font-medium">Date Range:</span> {plan.date_range}</p>
        <p><span className="font-medium">Days:</span> {plan.days.length}</p>
      </div>
      {plan.is_draft && (
        <div className="mt-4">
          <span className="inline-block px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">Draft</span>
        </div>
      )}
    </Card>
  );
};

export default LessonPlanCard;